import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CircleUserRound,
  Link,
  Trash,
  ChevronDown,
  CheckIcon,
} from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getUnexpiredToken, logAxiosError, useCanvasId } from "@/utils/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser, useSession } from "@clerk/clerk-react";
import { Clipboard } from "lucide-react";

interface Props {
  type: "canvas" | "project";
  projectId: string;
}

export function AddMembers({ type, projectId }: Props) {
  const [email, setEmail] = useState("");
  const [memberships, setMemberships] = useState([]);
  const [invitationCreated, setInvitationCreated] = useState(false);
  const [fetchingMemberships, setFetchingMemberships] = useState(false);
  const { session } = useSession();
  const { user } = useUser();

  const canvasId = useCanvasId();

  const submitEmail = async () => {
    if (!email) return;
    if (invitationCreated) return;
    if (!projectId) return;

    try {
      setInvitationCreated(true);

      const supabaseJWT = await getUnexpiredToken(session);

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${projectId}/invitations`,
        {
          email: email.trim(),
          canvasId,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      setTimeout(() => {
        setInvitationCreated(false);
        fetchMemberships();
      }, 500);
    } catch (error) {
      logAxiosError("Failed to send invitation", error);
      toast.error("Failed to send invitation");
    } finally {
      setInvitationCreated(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      setFetchingMemberships(true);

      const supabaseJWT = await getUnexpiredToken(session);

      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${projectId}/memberships`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );
      setMemberships(
        response.data.memberships.sort((a, b) => {
          // user first
          if (a.user === user?.primaryEmailAddress?.emailAddress) return -1;
          // then owners
          if (a.role === "owner" && b.role === "pending") return -1;
          // rest
          return a.role?.localeCompare(b.role);
        }),
      );
    } catch (error) {
      toast.error("Failed to fetch memberships");
    } finally {
      setFetchingMemberships(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      return;
    }

    fetchMemberships();
  }, [projectId]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Ensure email doesn't contain newlines
    e.target.value = e.target.value.replace(/\n/g, "");
    setEmail(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      submitEmail();
    }
  };

  return (
    <>
      <div className="px-6 py-4 flex flex-col">
        <div className="text-sm font-medium text-white mb-2 flex flex-row justify-between items-center">
          Invite collaborators
        </div>
        <div className="flex w-full items-center justify-between space-x-2">
          <div className="flex grow">
            <Input
              className="bg-secondary-n-950 text-white border-secondary-n-900 text-xs py-0 h-8"
              placeholder="Invite others by email"
              value={email}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex">
            <Button
              type="submit"
              className="bg-primary-0 hover:bg-primary-100 text-white whitespace-nowrap text-xs h-8 font-medium rounded-md"
              onClick={submitEmail}
              disabled={invitationCreated}
            >
              {invitationCreated ? "Sent" : "Invite"}
            </Button>
          </div>
        </div>
      </div>
      <ScrollArea viewportClassName="max-h-64 p-4">
        {fetchingMemberships ? (
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-row gap-x-3 items-center">
                <Skeleton className="w-8 h-8 rounded-full" />
                <Skeleton className="w-44 h-6" />
              </div>
              <Skeleton className="w-12 h-6" />
            </div>
            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-row gap-x-3 items-center">
                <Skeleton className="w-8 h-8 rounded-full" />
                <Skeleton className="w-36 h-6" />
              </div>
              <Skeleton className="w-10 h-6" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-y-2">
            {memberships.map((membership: any) => {
              const isUser =
                membership.user === user?.primaryEmailAddress?.emailAddress;
              const role = membership.role
                ? membership.role.slice(0, 1).toUpperCase() +
                  membership.role.slice(1)
                : "";
              return (
                <div
                  className="flex flex-row justify-between items-center py-1"
                  key={membership.user}
                >
                  <div className="flex flex-row gap-x-3 items-center">
                    {membership.picture ? (
                      <img
                        src={membership.picture}
                        width={24}
                        height={24}
                        alt="avatar"
                        className="rounded-md"
                      />
                    ) : (
                      <CircleUserRound className="w-6 h-6" strokeWidth={1} />
                    )}
                    <div className="text-xs text-white">
                      {membership.user}{" "}
                      <span className="text-secondary-700">
                        {isUser ? "(you)" : ""}
                      </span>
                    </div>
                  </div>
                  {isUser && (
                    <div className="text-xs text-white px-2">{role}</div>
                  )}
                  {!isUser && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="text-xs text-white px-2 py-3 h-6 hover:bg-secondary-n-900 border border-secondary-n-900"
                          variant="outline"
                        >
                          {role} <ChevronDown className="ml-1 h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        {role !== "Pending" && (
                          <DropdownMenuItem className="text-xs cursor-pointer">
                            {role}
                            <CheckIcon
                              className={"ml-auto h-4 w-4 text-primary-0"}
                            />
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-xs cursor-pointer text-destructive"
                          onClick={() => {
                            getUnexpiredToken(session).then((supabaseJWT) => {
                              axios
                                .post(
                                  `${
                                    import.meta.env.VITE_BACKEND_BASE_URL
                                  }/projects/${projectId}/removeUser`,
                                  {
                                    email: membership.user,
                                  },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${supabaseJWT}`,
                                    },
                                  },
                                )
                                .then(() => {
                                  toast.success("User removed");
                                  fetchMemberships();
                                })
                                .catch((error) => {
                                  toast.error("Failed to remove user");
                                  logAxiosError("Failed to remove user", error);
                                });
                            });
                          }}
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </>
  );
}
